<template>
  <v-container class="referrals-page py-8">
    <div class="d-flex flex-wrap justify-space-between align-center mb-6">
      <div>
        <div class="text-overline primary--text">
          {{ $t("\n          GROW YOUR CIRCLE\n        ") }}
        </div><h1 class="text-h4 mb-2">
          {{ $t("\n          Referrals & affiliates\n        ") }}
        </h1><p class="mb-0">
          {{ $t("\n          Help someone start preserving their story.\n        ") }}
        </p>
      </div>
      <v-btn
        v-if="data && data.admin"
        to="/admin/affiliates"
        outlined
        color="primary"
      >
        {{ $t("\n        Administration\n      ") }}
      </v-btn>
    </div>
    <v-alert
      v-if="error"
      type="error"
      outlined
    >
      {{ error }} <v-btn
        text
        small
        @click="load"
      >
        {{ $t("\n        Try again\n      ") }}
      </v-btn>
    </v-alert>
    <v-alert
      v-if="message"
      type="success"
      outlined
      dismissible
    >
      {{ message }}
    </v-alert>
    <v-progress-linear
      v-if="loading"
      indeterminate
      :aria-label="$t(&quot;Loading referrals&quot;)"
    ></v-progress-linear>
    <template v-if="data">
      <v-card
        v-if="!data.affiliate"
        outlined
        class="pa-6 mb-6"
      >
        <div class="text-overline primary--text">
          {{ $t("\n          PARTNER PROGRAM\n        ") }}
        </div><h2 class="text-h5 mb-3">
          {{ $t("\n          Become an affiliate\n        ") }}
        </h2>
        <p>{{ $t("Earn ") }}{{ data.rules.commission_bps / 100 }}{{ $t("% on all eligible purchases, including subscriptions, renewals, upgrades and AI credit packs, for ") }}{{ data.rules.commission_months }}{{ $t(" months from each customer's first purchase. No referral limit.") }}</p>
        <p>{{ $t("Your attribution also follows friends invited by your customers, at every level. Commissions go to the originating affiliate. Customers joining through your affiliate link receive ") }}{{ data.rules.friend_credits }}{{ $t(" AI credits after their first subscription payment and the ") }}{{ data.rules.hold_days }}{{ $t("-day validation period.") }}</p>
        <p v-if="requiresPaidSubscription">
          {{ $t("An active paid subscription is required when you apply. Later plan changes do not affect your affiliate status or commissions.") }}
        </p>
        <p v-else>
          {{ $t("Free accounts can apply. Approval is required before your affiliate link becomes available.") }}
        </p>
        <v-alert
          v-if="!canApply"
          type="info"
          outlined
          class="mb-4"
        >
          {{ eligibilityMessage }}
        </v-alert>
        <div
          v-if="!canApply"
          class="d-flex flex-wrap"
          style="gap: 12px"
        >
          <v-btn
            v-if="eligibilityReason !== 'unavailable'"
            color="primary"
            to="/billing"
          >
            {{ eligibilityReason === 'billing_review' ? $t("Manage billing") : $t("View paid plans") }}
          </v-btn>
          <v-btn
            outlined
            :loading="loading"
            @click="load"
          >
            {{ $t("Check eligibility") }}
          </v-btn>
        </div>
        <v-textarea
          v-if="canApply"
          v-model="application"
          :label="$t(&quot;How will you promote Self Biography?&quot;)"
          outlined
          counter="2000"
          :maxlength="2000"
          rows="3"
        ></v-textarea>
        <v-btn
          v-if="canApply"
          color="primary"
          :disabled="!canApply || application.trim().length < 10"
          :loading="busy"
          @click="apply"
        >
          {{ $t("\n          Apply to become an affiliate\n        ") }}
        </v-btn>
      </v-card>
      <template v-else>
        <v-card
          outlined
          class="pa-6 mb-6"
        >
          <div class="d-flex flex-wrap align-center mb-3">
            <h2 class="text-h5 mr-3">
              {{ $t("\n              Your affiliate account\n            ") }}
            </h2><v-chip
              small
              :color="data.affiliate.status === 'active' ? 'success' : 'warning'"
              outlined
            >
              {{ data.affiliate.status }}
            </v-chip>
          </div>
          <p>{{ $t("Customers joining through your affiliate link receive ") }}{{ data.rules.friend_credits }}{{ $t(" AI credits under the same validation and expiration rules as friend invitations. You earn commission on all eligible purchases, including AI credit packs.") }}</p>
          <p v-if="data.affiliate.status === 'pending'">
            {{ $t("\n            Your application is awaiting review. Your affiliate link becomes available after approval.\n          ") }}
          </p>
          <p v-else-if="data.affiliate.status !== 'active'">
            {{ $t("\n            Your affiliate link and payouts are unavailable. Contact support for a review.\n          ") }}
          </p>
          <template v-else>
            <v-text-field
              :value="data.affiliate.link"
              :label="$t(&quot;Your affiliate link&quot;)"
              readonly
              outlined
              hide-details
              class="mb-3"
            ></v-text-field><v-btn
              color="primary"
              @click="copy(data.affiliate.link)"
            >
              {{ $t("\n              Copy affiliate link\n            ") }}
            </v-btn>
          </template>
          <p class="text-caption mt-4 mb-0">
            {{ data.rules.commission_bps / 100 }}{{ $t("% for new attributed customers · ") }}{{ data.rules.commission_months }}{{ $t(" months · ") }}{{ data.rules.hold_days }}{{ $t("-day release · ") }}{{ money(data.rules.payout_minimum) }}{{ $t(" minimum payout. Terms are fixed when each customer joins.\n          ") }}
          </p>
        </v-card>
        <v-row class="mb-5">
          <v-col
            v-for="stat in affiliateStats"
            :key="stat.label"
            cols="6"
            md="3"
          >
            <v-card
              outlined
              class="pa-5"
            >
              <div class="text-body-2 mb-2">
                {{ stat.label }}
              </div><strong class="text-h4">{{ stat.value }}</strong>
            </v-card>
          </v-col>
        </v-row>
        <v-card
          outlined
          class="mb-6"
        >
          <v-card-title>{{ $t("Recent commissions") }}</v-card-title><v-card-subtitle>{{ $t("Your latest 100 purchases, including subscriptions, AI credit packs and inherited referrals.") }}</v-card-subtitle>
          <v-data-table
            :headers="commissionHeaders"
            :items="data.affiliate.commissions"
            item-key="invoice_id"
            :items-per-page="10"
            :no-data-text="$t(&quot;Your commissions will appear after a referred customer pays.&quot;)"
          >
            <template v-slot:item.payment_kind="{ item }">
              {{ item.payment_kind === 'purchase' ? $t("One-time purchase") : $t("Subscription") }}
            </template>
            <template v-slot:item.paid_at="{ item }">
              {{ date(item.paid_at) }}
            </template>
            <template v-slot:item.commission_amount="{ item }">
              {{ money(item.commission_amount) }}
            </template>
            <template v-slot:item.available_at="{ item }">
              {{ date(item.available_at) }}
            </template>
            <template v-slot:item.status="{ item }">
              {{ commissionStatus(item) }}
            </template>
          </v-data-table>
        </v-card>
        <v-card
          outlined
          class="mb-6"
        >
          <v-card-title>{{ $t("Payout history") }}</v-card-title><v-data-table
            :headers="payoutHeaders"
            :items="data.affiliate.payouts"
            item-key="id"
            :items-per-page="10"
            :no-data-text="$t(&quot;No payouts yet.&quot;)"
          >
            <template v-slot:item.amount="{ item }">
              {{ money(item.amount) }}
            </template><template v-slot:item.created_at="{ item }">
              {{ date(item.created_at) }}
            </template>
          </v-data-table>
        </v-card>
      </template>
      <v-card
        outlined
        class="pa-6 mb-6 referral-hero"
      >
        <div class="text-overline primary--text">
          {{ $t("\n          INVITE A FRIEND\n        ") }}
        </div>
        <h2 class="text-h5 mb-3">
          {{ data.rules.friend_credits }}{{ $t(" AI credits for each of you\n        ") }}
        </h2>
        <p>{{ $t("Share your link with a new member. After their first subscription payment and a ") }}{{ data.rules.hold_days }}{{ $t("-day validation period, you both receive credits. There is no invitation limit.") }}</p>
        <v-text-field
          :value="data.friendLink"
          :label="$t(&quot;Your invitation link&quot;)"
          readonly
          outlined
          hide-details
          class="mb-3"
        ></v-text-field>
        <v-btn
          color="primary"
          @click="copy(data.friendLink)"
        >
          {{ $t("\n          Copy invitation link\n        ") }}
        </v-btn>
        <p class="text-caption mt-3 mb-0">
          {{ $t("\n          Links apply to signups within 30 days. Your friend must subscribe within 90 days of signup. Reward credits expire ") }}{{ data.rules.credit_expiry_days }}{{ $t(" days after release. Existing invitations retain their original terms.\n        ") }}
        </p>
      </v-card>
      <v-row class="mb-5">
        <v-col
          v-for="stat in friendStats"
          :key="stat.label"
          cols="6"
          md="3"
        >
          <v-card
            outlined
            class="pa-5"
          >
            <div class="text-body-2 mb-2">
              {{ stat.label }}
            </div><strong class="text-h4">{{ stat.value }}</strong>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { t } from '@/i18n'

import referrals from '@/services/referrals'

export default {
  name: 'Referrals',
  data: () => ({
    data: null,
    loading: false,
    busy: false,
    error: '',
    message: '',
    application: '',
    commissionHeaders: [{ get text() { return t('Purchase') }, value: 'invoice_id' }, { get text() { return t('Type') }, value: 'payment_kind' }, { get text() { return t('Payment date') }, value: 'paid_at' }, { get text() { return t('Commission') }, value: 'commission_amount' }, { get text() { return t('Release date') }, value: 'available_at' }, { get text() { return t('Status') }, value: 'status', sortable: false }],
    payoutHeaders: [{ get text() { return t('Date') }, value: 'created_at' }, { get text() { return t('Amount') }, value: 'amount' }, { get text() { return t('Status') }, value: 'status' }, { get text() { return t('Reference') }, value: 'payment_reference' }],
  }),
  computed: {
    requiresPaidSubscription() { return Boolean(this.data && Number(this.data.rules.affiliate_subscription_required) === 1) },
    canApply() { return Boolean(this.data && this.data.applicationEligibility && this.data.applicationEligibility.eligible === true) },
    eligibilityReason() { return (this.data && this.data.applicationEligibility && this.data.applicationEligibility.reason) || 'unavailable' },
    eligibilityMessage() {
      if (this.eligibilityReason === 'unavailable') return t('We could not verify your application eligibility. Check again before applying.')
      if (this.eligibilityReason === 'billing_review') return t('Your billing account needs review before you can apply.')

      return t('Choose an active paid plan to apply. The Free plan and AI credit packs do not qualify.')
    },
    friendStats() {
      const credits = status => {
        const row = this.data.friend.rewards.find(r => r.status === status)

        return row ? Number(row.credits) : 0
      }

      return [{ get label() { return t('Friends invited') }, value: this.data.friend.invited }, { get label() { return t('Subscriptions started') }, value: this.data.friend.converted }, { get label() { return t('Pending credits') }, value: credits('pending') }, { get label() { return t('Credits awarded') }, value: credits('granted') }]
    },
    affiliateStats() {
      const a = this.data.affiliate

      return [{ get label() { return t('Attributed members') }, value: a.network }, { get label() { return t('Pending commission') }, value: this.money(a.totals.pending) }, { get label() { return t('Available balance') }, value: this.money(a.totals.available) }, { get label() { return t('Paid out') }, value: this.money(a.totals.paid) }]
    },
  },
  mounted() { this.load() },
  methods: {
    money(value) { return new Intl.NumberFormat(this.$locale(), { style: 'currency', currency: 'USD' }).format((value || 0) / 100) },
    date(value) { return value ? new Date(value).toLocaleDateString(this.$locale()) : '—' },
    commissionStatus(item) {
      if (item.disputed) return t('Under review')
      if (item.refunded_amount) return t('Adjusted after refund')
      if (item.reserved_payout_id) return t('Payout prepared')
      if (item.settled_amount >= item.commission_amount) return t('Settled')

      return new Date(item.available_at) > new Date() ? t('Pending') : t('Available')
    },
    async load() {
      this.loading = true; this.error = ''
      try { this.data = await referrals.me() } catch (error) { this.error = error.response ? (error.response.data.message || t('Referrals are temporarily unavailable.')) : error.message } finally { this.loading = false }
    },
    async copy(value) {
      try { await navigator.clipboard.writeText(value); this.message = t('Link copied.') } catch (_) { this.error = 'Select the link above and copy it manually.' }
    },
    async apply() {
      if (!this.canApply || this.busy) return
      this.busy = true; this.error = ''
      try { await referrals.apply(this.application); this.message = t('Application submitted for review.'); await this.load() } catch (error) {
        const response = error.response && error.response.data
        if (response && response.error === 'AFFILIATE_PAID_PLAN_REQUIRED') await this.load()
        this.error = (response && response.message) || t('Could not submit your application.')
      } finally { this.busy = false }
    },
  },
}
</script>

<style scoped>
.referrals-page { max-width: 1180px; }
.referral-hero { border-top: 3px solid var(--v-primary-base) !important; }
</style>
